import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authService, type AuthUser } from "@/services/auth.service";
import { gpsDriveAuthService, type GpsDriveUser } from "@/services/gpsdrive-auth.service";
import { vehiclesService, setStickyUpdatedHandler } from "@/services/vehicles.service";
import { clearUsersListCache } from "@/services/users.service";
import { clearUsersSummaryCache } from "@/services/gpsdrive-users-summary.service";
import { clearCompanySubUserTraccarIdsCache } from "@/services/gpsdrive-sub-users.service";
import { setUnauthorizedHandler } from "@/lib/api/client";
import { recoverTraccarSession, setTraccarSessionRecoveryEnabled } from "@/lib/api/traccar-session-recovery";
import {
  GPSDRIVE_API_URL,
  GpsDriveApiError,
  lastRefreshFailureWasClean,
  setGpsDriveImpersonating,
  setGpsDriveImpersonationExpiredHandler,
  setGpsDriveSessionExpiredHandler,
  setGpsDriveSessionRevokedHandler,
  setWrongCompanyDomainHandler,
  startGpsDriveKeepAlive,
  WRONG_COMPANY_DOMAIN,
} from "@/lib/api/gpsdrive-client";
import { queryKeys } from "@/hooks/queries";
import { gpsDrivePersister } from "@/lib/query-persister";
import { toast } from "sonner";
import { setCurrentAccountType } from "@/lib/current-account";
import { applyCompanyTints } from "@/lib/vehicle-icons/colors";

// "ok"/"expired" só fazem sentido depois que a primeira tentativa (boot ou
// login) já rodou; "unavailable" é para quando VITE_GPSDRIVE_API_URL nem
// está configurada (GPS Drive Backend ainda não implantado neste
// ambiente) — nesse caso nunca tentamos logar nele, então nunca vira
// "expired" (que implicaria "chegou a funcionar uma vez").
export type GpsDriveSessionStatus = "loading" | "ok" | "expired" | "unavailable";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Falha de rede (fetch não completou) ou 5xx/0 do backend — transitório,
// vale tentar de novo. Um 401/403 limpo (credenciais erradas de verdade)
// nunca é retry-ável, precisa dar o erro na hora pro usuário corrigir.
function isTransientGpsDriveError(err: unknown): boolean {
  if (err instanceof GpsDriveApiError) return err.status === 0 || err.status >= 500;
  return true;
}

// Mesmo incidente de produção 2026-09-01 que motivou o retry em
// performRefresh() (gpsdrive-client.ts) — aqui cobre o LOGIN inicial (que
// nunca passa por performRefresh, usa email+senha, não cookie/refresh
// token). Sem isso, uma falha transitória bem no instante do login (ex.:
// backend momentaneamente sem conseguir falar com o Traccar) já nascia
// mostrando "sessão de gestão expirada" pro usuário, mesmo com a senha
// certa e a rede voltando ao normal segundos depois.
const GPSDRIVE_LOGIN_RETRY_DELAYS_MS = [1000, 2000];

// Aviso que a tela de login mostra depois de uma saída forçada (ex.: entrou
// no domínio de outra empresa).
export const LOGIN_NOTICE_KEY = "gpsdrive.loginNotice";

interface AuthContextValue {
  // Sessão do Traccar — rastreamento/mapa/comandos/etc. Nunca afetada por
  // falha da sessão do GPS Drive Backend (ver gpsDriveStatus abaixo).
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;

  // Sessão do GPS Drive Backend — cadastro/foto/dados de gestão. Some/
  // expira de forma totalmente independente da sessão do Traccar acima:
  // se cair, o mapa/rastreamento continuam funcionando normalmente. Desde
  // 2026-08-28, cair sozinha quase nunca chega a virar "expired" visível:
  // gpsdrive-client.ts#performRefresh tenta se reautenticar em silêncio
  // via /auth/session-refresh (prova a identidade pela sessão do Traccar,
  // sem senha) antes de desistir — "expired" só aparece de fato se o
  // Traccar TAMBÉM não tiver mais sessão válida, caso em que o AuthGate já
  // está redirecionando pra /login de qualquer forma.
  gpsDriveUser: GpsDriveUser | null;
  gpsDriveStatus: GpsDriveSessionStatus;
  // Reautentica SÓ o GPS Drive Backend, sem tocar na sessão do Traccar —
  // usado pelo aviso de "sessão de gestão expirada" (nunca força um login
  // completo de novo só porque essa metade caiu).
  reauthenticateGpsDrive: (email: string, password: string) => Promise<void>;
  // Tela obrigatória de troca de senha (admin/funcionário com senha fraca).
  changeOwnPassword: (input: { currentPassword: string; newPassword: string; phone?: string }) => Promise<void>;

  // "Logar como" (Controle de Usuário, painel expandido de Usuários) — só
  // troca a sessão do GPS Drive Backend (gpsDriveUser acima); a sessão do
  // Traccar (mapa/rastreamento) continua sendo a do administrador o tempo
  // todo, nunca a do usuário impersonado (ver AuthService#impersonate no
  // backend — não existe um jeito seguro de "logar como" no Traccar sem a
  // senha da pessoa). `impersonating` guarda quem é o administrador real
  // enquanto isso, pra "Voltar para administrador" e pro aviso fixo na tela.
  impersonating: { admin: GpsDriveUser } | null;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [gpsDriveUser, setGpsDriveUser] = useState<GpsDriveUser | null>(null);
  const [gpsDriveStatus, setGpsDriveStatus] = useState<GpsDriveSessionStatus>(
    GPSDRIVE_API_URL ? "loading" : "unavailable",
  );
  const [impersonating, setImpersonating] = useState<{ admin: GpsDriveUser } | null>(null);
  useEffect(() => {
    setCurrentAccountType(gpsDriveUser?.accountType ?? null);
  }, [gpsDriveUser?.accountType]);
  useEffect(() => {
    applyCompanyTints(gpsDriveUser?.companyId);
  }, [gpsDriveUser?.companyId]);
  // Estado da troca de sessão do Traccar durante "Logar como" (ver
  // impersonate/stopImpersonating abaixo) — ref, não state: é só um dado
  // pra restaurar depois, nunca precisa disparar re-render sozinho.
  // `switched` distingue "nunca tentei" de "tentei e falhou" (ex.:
  // TRACCAR_URL não configurado) — só tenta restaurar se realmente trocou.
  const traccarSessionSwitchRef = useRef<{ switched: boolean; originalCookie: string | null }>({
    switched: false,
    originalCookie: null,
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    authService
      .getSession()
      // Sem sessão do Traccar no boot (ex.: Traccar reiniciou desde a
      // última visita): tenta recriá-la pela sessão do GPS Drive antes de
      // mandar pro login — ver traccar-session-recovery.ts.
      .then(async (u) => {
        if (u) return u;
        return (await recoverTraccarSession()) ? authService.getSession() : null;
      })
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Recuperação de sessão do GPS Drive Backend no boot (F5, aba nova) —
  // independente da recuperação da sessão do Traccar acima, em paralelo,
  // uma nunca espera a outra. Sem VITE_GPSDRIVE_API_URL configurada, nem
  // tenta (permanece "unavailable" — ver estado inicial acima).
  // recoverSession() já tenta de novo sozinho em caso de falha transitória
  // (ver performRefresh em gpsdrive-client.ts) — só chega a "expired" aqui
  // se realmente não tiver sessão válida nem no GPS Drive Backend nem no
  // Traccar (cookie), ou se os retries todos esgotarem.
  useEffect(() => {
    if (!GPSDRIVE_API_URL) return;
    let cancelled = false;
    gpsDriveAuthService
      .recoverSession()
      .then((u) => {
        if (cancelled) return;
        setGpsDriveUser(u);
        setGpsDriveStatus(u ? "ok" : "expired");
      })
      .catch(() => {
        if (!cancelled) setGpsDriveStatus("expired");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-recuperação em segundo plano (pedido 2026-09-01: "tudo tem que
  // funcionar 100% automático... nunca pode aparecer isso") — enquanto a
  // sessão de gestão estiver "expired" mas a sessão do Traccar continuar
  // válida (`user`), tenta se reautenticar sozinho a cada 20s via
  // recoverSession() (cookie de refresh → session-refresh via Traccar,
  // NUNCA pede senha). Assim que resolver, o banner some sozinho, sem
  // nenhuma ação do usuário. Só continua mostrando o banner se o Traccar
  // TAMBÉM tiver caído (aí o AuthGate já redireciona pra /login de
  // qualquer forma, ver gpsDriveStatus acima) ou enquanto a instabilidade
  // realmente persistir — nesse caso cada tentativa já leva os retries de
  // performRefresh() consigo, nunca desiste na primeira falha de rede.
  //
  // Aba aberta há muito tempo (bug 2026-10-23: "aparece que meu login
  // expirou porém o site não recarregou sozinho, está aberto desde ontem"):
  // se a sessão de gestão estava OK nesta aba e morreu de verdade (refresh
  // e session-refresh com 401/403 limpo, não queda de rede), não adianta
  // ficar no banner com a tela sem dados — sai e volta pro login com aviso.
  // Só vale pra sessão que já esteve OK aqui: logo após o login (credenciais
  // divergentes, TD-015) continua o banner de sempre, sem loop.
  const gpsDriveWasOkRef = useRef(false);
  useEffect(() => {
    if (gpsDriveStatus === "ok") gpsDriveWasOkRef.current = true;
  }, [gpsDriveStatus]);

  // Outra conta entrou em OUTRA aba do mesmo navegador (incidente
  // 2026-10-29, Politracker: login da cliente marcella numa aba e a aba do
  // admin continuou mostrando "admin" — mas o cookie do Traccar é um só por
  // navegador, então ela passou a falar com o Traccar como a cliente e
  // "Novo veículo" dava 400 Write access denied). Ao voltar pra esta aba,
  // confere de quem é a sessão do Traccar; se mudou, recarrega e a aba
  // passa a ser da conta que entrou por último (como Gmail etc.).
  useEffect(() => {
    if (!user || impersonating) return;
    const expectedId = user.id;
    let checking = false;
    const check = () => {
      if (document.visibilityState !== "visible" || checking) return;
      checking = true;
      authService
        .getSession()
        .then((current) => {
          if (current && current.id !== expectedId) window.location.reload();
        })
        .catch(() => undefined)
        .finally(() => {
          checking = false;
        });
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [user, impersonating]);
  useEffect(() => {
    if (gpsDriveStatus !== "expired" || !user || !gpsDriveWasOkRef.current || impersonating) return;
    let cancelled = false;
    gpsDriveAuthService
      .recoverSession()
      .then((u) => {
        if (cancelled) return;
        if (u) {
          setGpsDriveUser(u);
          setGpsDriveStatus("ok");
          return;
        }
        if (!lastRefreshFailureWasClean()) return; // rede/5xx: o loop abaixo segue tentando
        gpsDriveWasOkRef.current = false;
        try {
          sessionStorage.setItem(LOGIN_NOTICE_KEY, "Sua sessão expirou. Entre novamente para continuar.");
        } catch {
          // sem sessionStorage só perde o aviso
        }
        void logoutRef.current();
      })
      .catch(() => {
        // segue no loop abaixo
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsDriveStatus, user]);

  useEffect(() => {
    if (gpsDriveStatus !== "expired" || !user) return;
    const interval = setInterval(() => {
      gpsDriveAuthService
        .recoverSession()
        .then((u) => {
          if (u) {
            setGpsDriveUser(u);
            setGpsDriveStatus("ok");
          }
        })
        .catch(() => {
          // segue tentando no próximo tick
        });
    }, 20_000);
    return () => clearInterval(interval);
  }, [gpsDriveStatus, user]);

  // Encerra a sessão no cliente: derruba o cache do React Query (o que também
  // interrompe qualquer polling/refetch agendado, já que as queries deixam de
  // existir) e limpa o usuário. Também limpa o cache sticky de blocked/
  // ignition (vehicles.service) — ele vive em memória do módulo, sobrevive à
  // troca de usuário na mesma aba (não há reload de página aqui), então sem
  // isso um segundo usuário logando herdaria blocked/ignition vistos pela
  // sessão anterior.
  //
  // gpsDrivePersister.removeClient() (pedido 2026-09-03, cache do React
  // Query agora sobrevive ao F5 via localStorage — ver
  // lib/query-persister.ts): queryClient.clear() só limpa a MEMÓRIA; sem
  // isto, CPF/CNPJ, faturas e documentos do cliente anterior continuariam
  // salvos no localStorage do navegador/computador até alguém logar de novo
  // e um fetch de verdade sobrescrever — problema real em computador
  // compartilhado (recepção, mais de um operador). Comentário antigo dizia
  // "não mexe em localStorage, são só preferências locais" — deixou de ser
  // verdade a partir desta mudança.
  const clearSession = useCallback(() => {
    queryClient.clear();
    gpsDrivePersister.removeClient();
    setUser(null);
    vehiclesService.clearStickyState();
    clearUsersListCache();
    clearUsersSummaryCache();
    clearCompanySubUserTraccarIdsCache();
  }, [queryClient]);

  useEffect(() => {
    // Qualquer chamada real à API que retorne 401 (sessão expirada/inválida)
    // encerra a sessão no cliente — o AuthGate reage a isso redirecionando
    // para /login.
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    // Callback separado do de cima de propósito (ver gpsdrive-client.ts) —
    // só marca a sessão de gestão como expirada, nunca deploga do Traccar
    // nem limpa o React Query inteiro (mapa/posições continuam vivos).
    setGpsDriveSessionExpiredHandler(() => {
      setGpsDriveUser(null);
      setGpsDriveStatus("expired");
    });
    return () => setGpsDriveSessionExpiredHandler(null);
  }, []);

  // Renova a sessão de gestão antes de vencer (ver startGpsDriveKeepAlive).
  useEffect(() => startGpsDriveKeepAlive(), []);

  useEffect(() => {
    // O seed sticky (vehicles.service.ts) roda em segundo plano, fora do
    // fluxo de listFromTraccar — quando ele aprende um blocked/ignition
    // novo, pede este refetch pontual de ["vehicles"] pra UI não esperar o
    // próximo poll de 15s. invalidateQueries com essa chave casa por
    // prefixo, cobrindo tanto ["vehicles"] (mapa) quanto ["vehicles", tick]
    // (useVehicles) na mesma chamada.
    setStickyUpdatedHandler(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
    });
    return () => setStickyUpdatedHandler(null);
  }, [queryClient]);

  const login = async (email: string, password: string) => {
    // Traccar primeiro — é o login "principal", nunca deve falhar por
    // causa do GPS Drive Backend estar fora do ar.
    const u = await authService.login(email, password);
    setTraccarSessionRecoveryEnabled(true);
    setUser(u);

    if (!GPSDRIVE_API_URL) return;
    for (let attempt = 0; attempt <= GPSDRIVE_LOGIN_RETRY_DELAYS_MS.length; attempt++) {
      try {
        const gpsUser = await gpsDriveAuthService.login(email, password);
        setGpsDriveUser(gpsUser);
        setGpsDriveStatus("ok");
        return;
      } catch (err) {
        // Usuário de outra empresa no domínio próprio de uma empresa: sai do
        // rastreamento também e a tela de login mostra o aviso do backend.
        if (err instanceof GpsDriveApiError && err.code === WRONG_COMPANY_DOMAIN) {
          setTraccarSessionRecoveryEnabled(false);
          await authService.logout().catch(() => {});
          clearSession();
          throw err;
        }
        const isLastAttempt = attempt === GPSDRIVE_LOGIN_RETRY_DELAYS_MS.length;
        if (!isTransientGpsDriveError(err) || isLastAttempt) {
          // Credenciais podem divergir entre os dois sistemas (bancos de
          // usuário totalmente separados, ver TECH_DEBT.md#TD-015), ou a
          // instabilidade não passou mesmo depois dos retries — em nenhum
          // dos casos isso deve impedir o login no Traccar. As telas de
          // cadastro/foto mostram o aviso de sessão de gestão expirada e
          // oferecem reautenticar (e o loop de auto-recuperação abaixo
          // continua tentando sozinho em segundo plano).
          setGpsDriveUser(null);
          setGpsDriveStatus("expired");
          return;
        }
        await sleep(GPSDRIVE_LOGIN_RETRY_DELAYS_MS[attempt]);
      }
    }
  };

  const changeOwnPassword = async (input: { currentPassword: string; newPassword: string; phone?: string }) => {
    const gpsUser = await gpsDriveAuthService.changePassword(input);
    setGpsDriveUser(gpsUser);
    setGpsDriveStatus("ok");
  };

  const reauthenticateGpsDrive = async (email: string, password: string) => {
    const gpsUser = await gpsDriveAuthService.login(email, password);
    setGpsDriveUser(gpsUser);
    setGpsDriveStatus("ok");
  };

  // Guarda o admin ATUAL antes de trocar — nunca aninha (impersonar
  // durante uma impersonação já em curso reaproveita o mesmo admin
  // original, nunca "empilha" quem é o admin de verdade). queryClient.clear()
  // é essencial aqui: sem isso, telas que dependem do GPS Drive Backend
  // (permissões — chave fixa ["my-permissions"], perfil, avatar etc.)
  // continuavam mostrando os dados em cache do PRÓPRIO admin mesmo depois
  // do access token já ter trocado pro usuário alvo — bug relatado
  // 2026-08-28 ("logar como não muda nada"). Mesmo padrão de clearSession()
  // no logout() abaixo.
  const impersonate = async (userId: string) => {
    if (!gpsDriveUser) return;
    const admin = impersonating?.admin ?? gpsDriveUser;

    // Troca a sessão de RASTREAMENTO do Traccar (mapa/lista de veículos)
    // ANTES de trocar o access token de gestão abaixo — de propósito, ORDEM
    // IMPORTA: /impersonate/:userId/traccar-session exige a MESMA
    // permissão "users.impersonate" que só o ADMINISTRADOR tem; o usuário
    // ALVO normalmente não tem. gpsDriveAuthService.impersonate() logo
    // abaixo troca o access token em memória pro do alvo imediatamente —
    // se a chamada do Traccar viesse DEPOIS, ela sairia autenticada como o
    // ALVO e tomaria 403 (bug real 2026-08-28: "logar como" continuava
    // mostrando a frota do admin, e o console mostrava 403 nesta rota
    // especificamente). Sem isso, "Logar como" só trocava a parte de
    // gestão e o mapa continuava mostrando a frota inteira do
    // administrador, mesmo pra um cliente com 1 veículo vinculado.
    // Best-effort DE PROPÓSITO (nunca lança): se falhar por outro motivo
    // (ex.: TRACCAR_URL não configurado nesta implantação), a
    // impersonação de gestão abaixo ainda vale — só o mapa segue sendo o
    // do administrador nesse caso específico.
    try {
      const { originalCookie } = await gpsDriveAuthService.switchTraccarSession(userId);
      traccarSessionSwitchRef.current = { switched: true, originalCookie };
    } catch {
      traccarSessionSwitchRef.current = { switched: false, originalCookie: null };
    }

    const targetUser = await gpsDriveAuthService.impersonate(userId);
    queryClient.clear();
    // Mesmo motivo de clearSession() — sem isso, CPF/faturas/documentos do
    // admin (ou de quem estava impersonado antes) ficariam salvos no
    // localStorage e poderiam aparecer por um instante pro próximo alvo
    // impersonado, antes do fetch de verdade sobrescrever.
    gpsDrivePersister.removeClient();
    setImpersonating({ admin });
    setGpsDriveUser(targetUser);
    // Precisa vir DEPOIS do impersonate() ter sucesso (ver comentário em
    // gpsdrive-client.ts#setGpsDriveImpersonating): a partir daqui, um 401
    // qualquer não tenta mais renovar sozinho (isso sempre devolveria o
    // admin, nunca o alvo) — só avisa via onImpersonationExpired.
    setGpsDriveImpersonating(true);
  };

  // Restaura a sessão do administrador via recoverSession() — usa o
  // refresh token/cookie dele, nunca tocado durante a impersonação (ver
  // gpsDriveAuthService.impersonate), então isto sempre traz o admin de
  // volta sem precisar pedir senha de novo. A sessão do TRACCAR é
  // restaurada à parte, reemitindo verbatim o cookie que o admin já tinha
  // antes de impersonar (ver impersonate() acima) — nunca precisa provar
  // nada de novo ao Traccar, só devolve o valor exato que já era dele.
  const stopImpersonating = async () => {
    if (!impersonating) return;
    setGpsDriveImpersonating(false);
    setImpersonating(null);
    const admin = await gpsDriveAuthService.recoverSession();

    if (traccarSessionSwitchRef.current.switched) {
      try {
        await gpsDriveAuthService.restoreTraccarSession(traccarSessionSwitchRef.current.originalCookie);
      } catch {
        // Pior caso: o navegador continua com a sessão do Traccar do
        // ALVO até o próximo login manual — não bloqueia a restauração da
        // sessão de gestão acima, que é sempre a parte crítica.
      } finally {
        traccarSessionSwitchRef.current = { switched: false, originalCookie: null };
      }
    }

    queryClient.clear();
    // Mesmo motivo de impersonate() acima — não deixa dado do usuário
    // impersonado (CPF/faturas/documentos) salvo no localStorage depois de
    // voltar pro admin.
    gpsDrivePersister.removeClient();
    setGpsDriveUser(admin);
    setGpsDriveStatus(admin ? "ok" : "expired");
  };

  useEffect(() => {
    // "Logar como" expira sozinho (access token de vida curta, nunca
    // renova — ver setGpsDriveImpersonating em gpsdrive-client.ts) —
    // quando isso acontece, encerra a impersonação de forma VISÍVEL
    // (restaura o admin + avisa) em vez de deixar o token trocar de volta
    // em silêncio enquanto a tela ainda diz "logado como X" (bug real
    // 2026-08-28). Redeclarado a cada troca de `impersonating` só pra
    // sempre capturar o stopImpersonating() da MESMA leva de estado —
    // nunca um closure velho apontando pra um `impersonating` já trocado.
    setGpsDriveImpersonationExpiredHandler(() => {
      toast.info("A sessão do usuário impersonado expirou — voltando para o administrador.");
      void stopImpersonating();
    });
    return () => setGpsDriveImpersonationExpiredHandler(null);
  }, [impersonating]);

  const logout = async () => {
    setTraccarSessionRecoveryEnabled(false);
    // Cancela requisições/polling em andamento antes de derrubar a sessão no
    // servidor, para não disparar novas chamadas com um cookie prestes a
    // ser invalidado.
    await queryClient.cancelQueries();
    try {
      await Promise.allSettled([
        authService.logout(),
        GPSDRIVE_API_URL ? gpsDriveAuthService.logout() : Promise.resolve(),
      ]);
    } finally {
      clearSession();
      setGpsDriveUser(null);
      setGpsDriveStatus(GPSDRIVE_API_URL ? "expired" : "unavailable");
      setImpersonating(null);
      setGpsDriveImpersonating(false);
    }
  };

  // Sessão já aberta (F5/aba nova) num domínio de empresa que não é a do
  // usuário: o backend recusa a renovação — sai de tudo e volta pro login
  // com o aviso (ver LOGIN_NOTICE_KEY em routes/login.tsx).
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    setGpsDriveSessionRevokedHandler(() => {
      toast.info("Sua senha foi alterada. Entre de novo com a senha nova.");
      void logoutRef.current();
    });
    return () => setGpsDriveSessionRevokedHandler(null);
  }, []);
  useEffect(() => {
    setWrongCompanyDomainHandler((message) => {
      try {
        sessionStorage.setItem(LOGIN_NOTICE_KEY, message);
      } catch {
        // sem sessionStorage só perde o aviso
      }
      void logoutRef.current();
    });
    return () => setWrongCompanyDomainHandler(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        gpsDriveUser,
        gpsDriveStatus,
        reauthenticateGpsDrive,
        changeOwnPassword,
        impersonating,
        impersonate,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
